import ArrowDownIcon from '@assets/icons/arrow-down.svg';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency, roundMicrosToNearestWholeCent } from '@/utils/money';
import { formatDate } from '@/utils/date';
import { HistoryListItem } from '@/components/history-list-item';
import { Orders_CreditRequestBatchFieldsFragment } from '@/types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { View } from 'react-native';

interface Props {
  creditRequest: Orders_CreditRequestBatchFieldsFragment;
  onPress: (creditRequestUUID: string) => void;
}

export const CreditRequestListItem: React.FC<Props> = ({
  creditRequest,
  onPress,
}) => {
  const [styles, theme] = useStyles();
  const { currencyCode } = useVariantPackInfoContext();
  const totalPriceInMicros = useMemo(() => {
    return creditRequest.credit_request_batch_line_items.reduce(
      (acc, lineItem) =>
        acc + lineItem.pack_price_in_micros * lineItem.quantity,
      0,
    );
  }, [creditRequest.credit_request_batch_line_items]);
  const totalPrice = formatCurrency(
    roundMicrosToNearestWholeCent(totalPriceInMicros),
    currencyCode,
  );

  return (
    <HistoryListItem
      bottomLeftComponent={
        <View style={styles.base}>
          <View
            style={[styles.icon, { backgroundColor: theme.sk.color.icon.blue }]}
          >
            <ArrowDownIcon
              height={12}
              stroke={theme.sk.color.icon.ondark}
              testID="credit-requested-icon"
              width={12}
            />
          </View>
          <Typography
            color={theme.sk.color.text.tertiary}
            textTransform="uppercase"
            variant="paragraph-sm-600"
          >
            {i18n.t('credit_requested')}
          </Typography>
        </View>
      }
      bottomRightText={creditRequest.customer_facing_id}
      onPress={() => onPress(creditRequest.uuid)}
      topLeftText={formatDate(creditRequest.created_at)}
      topRightText={totalPrice}
    />
  );
};

const useStyles = createStyles(() => ({
  base: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  icon: {
    alignItems: 'center',
    borderRadius: 16,
    height: 16,
    justifyContent: 'center',
    marginRight: 4,
    width: 16,
  },
}));
